import React, { useState } from "react";

import { API } from "aws-amplify";
import { sanitizeInbound } from "../../../../graphql/inbound/inboundCRUD";

import { Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  FormDataConsumer,
  SaveButton,
  useNotify,
  useRedirect,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  FilePaths,
  InboundState,
  InboundStatus,
  NoteType,
  WorkflowType,
} from "../../../../utils/constants";
import { getCustomFieldsToSave } from "../../../../components/common/CustomFields";
import { logError } from "../../../../utils/appUtils";
import { saveRemoveFiles } from "../../../../utils/fileUtils";
import { isBulkShipment } from "../../../../utils/loadTypeUtils";
import { normalizeValue } from "../../../../utils/utils";
import { calcNetWeight } from "../InboundUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

export const ValidateCreateAdviceButton = ({
  account,
  entryPoint,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [isSaving, setIsSaving] = useState(false);

  const handleValidateSave = async (formData) => {
    handleSave();
  };

  const handleSave = () => {
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    setIsSaving(true);

    try {
      const now = new Date().toISOString();

      data.placedDateTime = data.placedDateTime || null;
      data.notifyDateTime = data.notifyDateTime || null;
      data.scheduledDateTime = data.scheduledDateTime || null;
      data.createDateTime = now;
      data.releaseDateTime = data.releaseDateTime || null;

      const inbound = { ...data };

      /* ===============================
       * REQUIRED CORE FIELDS
       * =============================== */
      inbound.active = "true"; // String!
      inbound.status = InboundStatus.ADVICE_PROCESSING;
      inbound.state = InboundState.NEW;
      inbound.groups = window.appUser?.groups;
      inbound.reloadYardId = window.appUser?.reloadYardId;
      inbound.accountName = account.businessName;
      inbound.accountId = account.id;
      inbound.billToAccountId =
        inbound.billToAccountId || account.defaultBillToAccountId || account.id;

      inbound.daysOnDemurrageClock = 0;

      // NOTE: advisedBy is intentionally left unset here. The yard advised
      // itself, so there is no external submitter to notify — and its absence
      // is what marks this advice yard-originated (isExternalAdvice in
      // InboundUtils), which hides the manager Reject button. Stamping the
      // creating manager's email here would make the advice look
      // customer-submitted.

      /* ===============================
       * STRING FIELDS (never null)
       * =============================== */
      inbound.instructions = "";
      inbound.railCarNumber = inbound.railCarNumber || "";
      inbound.shipFrom = inbound.shipFrom || inbound.accountName;
      // GSI inboundShipmentByAccountByPoNumber: key attributes cannot be empty strings (see ValidateCreateButton invoiceId).
      const poTrimmed = (inbound.poNumber || "").trim();
      inbound.poNumber = poTrimmed || " ";
      inbound.normalizedPoNumber = normalizeValue(inbound.poNumber);

      inbound.lotType = account.lotType || inbound.lotType || "";

      inbound.lotNumberOverride = account?.lotNumberOverride;

      /* ===============================
       * OPTIONAL ID FIELDS
       * → OMIT if empty
       * =============================== */
      if (!inbound.areaId) delete inbound.areaId;
      if (!inbound.zoneId) delete inbound.zoneId;
      if (!inbound.timeslotId) delete inbound.timeslotId;
      if (!inbound.carrierEquipmentTypeId)
        delete inbound.carrierEquipmentTypeId;
      // Hash key of inboundShipmentByCarrierByStatusByDates. The carrier type
      // change handler sets it to null for non-truck loads, and the AppSync
      // create resolver writes null as a DynamoDB NULL, which the index rejects.
      if (!inbound.carrierId) delete inbound.carrierId;

      /* ===============================
       * WEIGHTS
       * =============================== */
      inbound.scaleWeightUnits = inbound.weightUnits;

      const hasWeights = inbound.inventories?.items?.every(
        (i) => i.weight != null && !isNaN(i.weight),
      );

      if (hasWeights) {
        const weightUOM =
          inbound.weightUnits ||
          inbound.inventories.items[0]?.weightUnits ||
          inbound.inventories.items[0]?.weightUOM;
        inbound.netWeight = calcNetWeight(inbound, null, weightUOM);
        inbound.weightUnits = weightUOM;
      }

      /* ===============================
       * WORKFLOW TYPE
       * =============================== */
      inbound.workflowType = isBulkShipment(
        inbound.reloadYardCommodityId,
        inbound.areaId,
      )
        ? WorkflowType.BULK
        : WorkflowType.ACCEPT_STORE;

      /* ===============================
       * CUSTOM FIELDS
       * =============================== */
      inbound.customFields = getCustomFieldsToSave(
        inbound.customFields,
        account?.inboundCustomFields,
      );

      /* ===============================
       * HISTORY
       * =============================== */
      inbound.history = [
        {
          text: "Created Inbound Advice.",
          type: NoteType.GENERIC,
          date: now,
          by: window.appUser?.fullName,
        },
      ];

      if (!inbound.notes?.length) {
        delete inbound.notes;
      } else {
        inbound.notes.forEach((n) => {
          n.type = NoteType.GENERIC;
          n.date = now;
          n.by = window.appUser?.fullName;
        });
      }

      /* ===============================
       * FILES
       * =============================== */
      await saveRemoveFiles(
        undefined,
        inbound,
        "attachments",
        `${FilePaths.INBOUND}/${Date.now()}`,
      );

      /* ===============================
       * SANITIZE SIDE EFFECTS
       * =============================== */
      delete inbound.customFieldSelect;
      delete inbound.manifestFile;

      const sanitized = sanitizeInbound(inbound);
      console.log("sanitized", sanitized);

      return sanitized;
    } catch (err) {
      logError(
        "inboundShipments:ValidateCreateAdviceButton:transform",
        err,
        notify,
      );
      return Promise.reject();
    }
  };

  const onSuccess = async ({ data }) => {
    try {
      const response = await API.post("transREST", "/parseInboundAdvice", {
        body: {
          ...data,
          user: window.appUser.email,
          reloadYardId: window.appUser.reloadYardId,
        },
      });
      if (response.error) throw response.error;

      if (data.stockImport) {
        notify("Inbound created successfully. Marked as PLACED", "warning");
      } else {
        notify("Inbound created successfully", "success");
      }
      redirect("/inboundShipments");
    } catch (error) {
      logError(
        "inboundShipments:ValidateCreateButton:onSuccess",
        error,
        notify,
        redirect,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    const message = error.errors[0]?.message || "";
    if (message?.startsWith("The variables input")) {
      const formState = form.getState();
      logError(
        "inboundShipments:ValidateCreateButton:data",
        formState.values,
        false,
      );
    }

    logError("inboundShipments:ValidateCreateButton:onFailure", error, notify);
  };

  // Pass ...props, not ...rest to FormDataConsumer to include handleSubmitWithRedirect from props
  return (
    <FormDataConsumer>
      {({ formData, ...props }) => (
        <>
          <Backdrop className={classes.backdrop} open={isSaving}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <SaveButton
            id="save-button"
            label="Save"
            submitOnEnter={false}
            undoable={false}
            saving={isSaving}
            handleSubmitWithRedirect={() => handleValidateSave(formData)}
            transform={transform}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
        </>
      )}
    </FormDataConsumer>
  );
};
